import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../../utils/api';
import Pagination, { getPageSize } from '../../Pagination';
import { fmtDateTime, money } from '../../../utils/region';
import OverflowChips, { OChip } from '../../OverflowChips';
import Fab from '../../Fab';
import type {
  AwBankLine, AwExpense, AwPayable, AwPayableBody, AwPayableDetail, AwPayableFilter, AwPayableFromUpload, AwPayablePage,
  AwPaymentMethod, AwPurchaseNoteKind, AwPurchaseNotePage, AwPurchaseNoteRow, AwPurchaseOrder, AwPurchaseOrderBody, AwPurchaseOrderDetail, AwPurchaseOrderFilter,
  AwPurchaseOrderLineBody, AwPurchaseOrderPage,
} from '../../../types';
import type { AwCtx } from '../ctx';
import { amt, btn, btnPrimary, card, dmy, errBox, inp, note, num, openBlob, pill, pillAmber, pillGreen, pillGrey, pillRed, sectionHead, table, td, th } from '../ui';
import { Dialog, Drawer, Loader, Stat, actBtn, cancelBtn, chip, chipOn, emptyLine, field, isoDate, lbl, r2, todayIso } from '../dialog';

// ── Purchases (owner 2026-10-13) ────────────────────────────────────────────────────────────────
// Three REGISTERS of the client company, read and written through /api/acct-workspace/payables and
// /purchase-orders: supplier bills owed (payables) with the payments made on them, the purchase
// debit/credit notes that adjust a payable, and purchase orders that may be converted into a bill.
// Nothing here is the firm's: not its bank tables, not the statutory ledger (manual entries stay its
// only feed), not the company's invoices or receipts. VAT and WHT are typed from the bill — the UI
// only checks they are non-negative and that WHT does not exceed the total. The ONLY arithmetic on
// this side is the register's own rule, shown live so the operator sees what the server will store:
// to pay = total − WHT; a PO line = qty × unit price; a PO total = Σ lines; all at 2 dp. Outstanding
// and status come from the server, never recomputed here.
//
// Supplier bills (merchant_expense_uploads) stay what they were: the bills the company uploaded,
// approved in its portal. An upload becomes a payable only when the operator says so ("Create bill"),
// which pre-fills the form with the upload's own OCR fields and links the two (one payable per upload).

type TF = (key: string, opts?: Record<string, unknown>) => string;
const PAY_FILTERS: AwPayableFilter[] = ['all', 'open', 'partial', 'paid', 'void'];
const PO_FILTERS: AwPurchaseOrderFilter[] = ['all', 'draft', 'approved', 'received', 'billed', 'cancelled'];
const METHODS: AwPaymentMethod[] = ['transfer', 'cash', 'cheque', 'other'];
const MAX_STR = 200;
const pillBlue: React.CSSProperties = { ...pill, color: '#1E40AF', background: '#EFF6FF', border: '1px solid #BFDBFE' };
const payPill = (s: string): React.CSSProperties => s === 'paid' ? pillGreen : s === 'partial' ? pillAmber : s === 'void' ? pillRed : pillGrey;
const poPill = (s: string): React.CSSProperties => s === 'billed' ? pillGreen : s === 'cancelled' ? pillRed : s === 'approved' ? pillBlue : s === 'received' ? pillAmber : pillGrey;
// Days past due: amber up to 60, red beyond — the aging pane's own reading of the same buckets.
const overduePill = (days: number): React.CSSProperties => days > 60 ? pillRed : pillAmber;
const when = (v: number) => v ? fmtDateTime(v) : '';
const isLive = (p: AwPayable) => p.status === 'open' || p.status === 'partial';
const fin = (s: string): number | null => { if (String(s).trim() === '') return null; const n = Number(s); return Number.isFinite(n) ? n : NaN; };
const okStr = (s: string) => s.length <= MAX_STR;
/** The route's error turned into a sentence; a 409 names the state the register is in. */
const perr = (e: any, t: TF): string => {
  const msg = String(e?.message || '');
  if (e?.status === 409) return `${t('aw_pay_err_conflict')}${msg ? ` — ${msg}` : ''}`;
  if (e?.status === 404) return t('aw_pay_err_not_found');
  return msg || t('aw_bank_err_generic');
};
const seg = (on: boolean): React.CSSProperties => ({ ...btn, flex: 1, justifyContent: 'center', ...(on ? { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' } : {}) });
const Foot = ({ onClose, onSubmit, busy, ready, icon, label, t }: { onClose: () => void; onSubmit: () => void; busy: boolean; ready: boolean; icon: string; label: string; t: TF }) => (
  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
    <button onClick={onClose} disabled={busy} style={cancelBtn}>{t('aw_bank_cancel')}</button>
    <button onClick={onSubmit} disabled={!ready || busy} style={{ ...btnPrimary, opacity: !ready || busy ? 0.5 : 1 }}>
      <i className={busy ? 'ti ti-loader' : icon} aria-hidden /> {label}
    </button>
  </div>
);
const Field = ({ label, children, span }: { label: string; children: React.ReactNode; span?: number }) => (
  <div style={span ? { gridColumn: `span ${span}` } : undefined}><label style={lbl}>{label}</label>{children}</div>
);

/** Every page of a list the server pages (limit 200 = its largest page), so a picker never stops
 *  silently at the newest 200 rows (client review 2026-10-13). The page cap is a safety stop only. */
async function loadAllPages<T>(fetchPage: (page: number) => Promise<{ rows: T[]; total: number }>): Promise<T[]> {
  const out: T[] = [];
  for (let page = 1; page <= 50; page++) {
    const d = await fetchPage(page);
    const rows = d.rows || [];
    out.push(...rows);
    if (!rows.length || out.length >= (Number(d.total) || 0)) break;
  }
  return out;
}
/** Every payable of the register, all statuses: the contract has no "payable for upload X" lookup and
 *  no notes list, so the upload → bill link and the notes register are read off the payables themselves.
 *  A bill is linked to its upload whatever its status — the server refuses a second payable on the same
 *  upload even when the first was voided. */
const loadAllPayables = (clientId: string): Promise<AwPayable[]> =>
  loadAllPages<AwPayable>(page => api.awPayables(clientId, { status: 'all', page, limit: 200 }));
const uploadedAt = (v: string | number): number => typeof v === 'number' ? v : (/^\d+$/.test(String(v || '')) ? Number(v) : (Date.parse(String(v || '')) / 1000 || 0));
/** The uploads a bill may be created from: this company's pending and approved uploads (the server
 *  filters one status per call, so two calls, each paged to the end) that no payable links yet, newest first. */
async function loadBillableUploads(clientId: string): Promise<AwExpense[]> {
  const [pending, approved, pays] = await Promise.all([
    loadAllPages<AwExpense>(page => api.awExpenses(clientId, 'pending', { page, limit: 200 })),
    loadAllPages<AwExpense>(page => api.awExpenses(clientId, 'approved', { page, limit: 200 })),
    loadAllPayables(clientId),
  ]);
  const linked = byUpload(pays);
  return [...pending, ...approved].filter(u => !linked.has(u.id)).sort((a, b) => uploadedAt(b.uploaded_at) - uploadedAt(a.uploaded_at));
}
/** The unmatched money-out lines of the company's statements, every page (the API pages by state only). */
async function loadUnmatchedDebitLines(clientId: string): Promise<AwBankLine[]> {
  const rows = await loadAllPages<AwBankLine>(page => api.awBankLines(clientId, { state: 'unmatched', page, limit: 200 }));
  return rows.filter(l => Number(l.debit) > 0);
}
/** The server's currency rule (readCurrency): exactly three letters, or blank for the company's default —
 *  checked here so the refusal is read before the request, not after it. */
const curOk = (c: string) => !c.trim() || /^[A-Za-z]{3}$/.test(c.trim());
/** A unit price as typed: 2 dp like money, more when the operator typed more — the server keeps a
 *  per-unit price as typed and rounds only the line amount. */
const unitPrice = (v: any) => { const dp = (String(v).split('.')[1] || '').length; return money(Number(v) || 0, Math.min(6, Math.max(2, dp))); };
const byUpload = (rows: AwPayable[]): Map<string, AwPayable> => {
  const m = new Map<string, AwPayable>();
  for (const p of rows) if (p.expense_upload_id) m.set(p.expense_upload_id, p);
  return m;
};
const uploadLabel = (u: AwExpense) => [u.doc_date ? dmy(u.doc_date) : '', u.vendor, u.amount === null || u.amount === undefined ? '' : amt(u.amount), u.filename].filter(Boolean).join(' · ');
const payableLabel = (p: AwPayable) => `${p.bill_no} · ${p.vendor_name} · ${amt(p.outstanding)} ${p.currency}`;

// ── New / edit bill dialog ──────────────────────────────────────────────────────────────────────
function BillDialog({ ctx, payable, upload, onClose, onSaved }: {
  ctx: AwCtx; payable?: AwPayable; upload?: AwExpense; onClose: () => void; onSaved: () => void;
}) {
  const { t } = useTranslation();
  const edit = !!payable;
  // A paid or void bill is finished: the server takes its note alone (409 on anything else), so the
  // form opens with every other field read-only and sends { note } by itself (client review 2026-10-13).
  const noteOnly = edit && !isLive(payable!);
  const [vendor, setVendor] = useState(payable?.vendor_name || '');
  const [taxId, setTaxId] = useState(payable?.vendor_tax_id || '');
  const [billNo, setBillNo] = useState(payable?.bill_no || '');
  const [billDate, setBillDate] = useState(payable?.bill_date || todayIso());
  const [dueDate, setDueDate] = useState(payable?.due_date || '');
  // Blank = the company's own default currency, applied by the server (readCurrency → currencyCode);
  // nothing is guessed from the bills already in the register.
  const [currency, setCurrency] = useState(payable?.currency || '');
  const [total, setTotal] = useState(payable ? String(payable.total_amount) : '');
  const [vat, setVat] = useState(payable ? String(payable.vat_amount) : '');
  const [wht, setWht] = useState(payable ? String(payable.wht_amount) : '');
  const [noteTxt, setNoteTxt] = useState(payable?.note || '');
  const [uploadId, setUploadId] = useState(upload?.id || '');
  const [options, setOptions] = useState<AwExpense[] | null>(edit ? [] : null);
  const [filling, setFilling] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // The picker lists this company's pending/approved uploads that have no payable yet (every page, not
  // the newest 200); the one handed in from Supplier bills is kept even when a reload would hide it (it
  // has no bill — that is why it is here).
  useEffect(() => {
    if (edit) return;
    let alive = true;
    loadBillableUploads(ctx.clientId)
      .then(rows => {
        if (!alive) return;
        if (upload && !rows.some(u => u.id === upload.id)) rows.unshift(upload);
        setOptions(rows);
      })
      .catch((e: any) => { if (alive) { setOptions(upload ? [upload] : []); setErr(perr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, edit]);   // eslint-disable-line react-hooks/exhaustive-deps
  const fill = async (id: string) => {
    setUploadId(id); setErr('');
    if (!id) return;
    setFilling(true);
    try {
      // The upload's own OCR fields, as stored — the server derives nothing and neither does this form.
      const d: AwPayableFromUpload = await api.awPayableFromUpload(ctx.clientId, id);
      setVendor(d.vendor_name || ''); setTaxId(d.vendor_tax_id || ''); setBillNo(d.bill_no || '');
      if (d.bill_date) setBillDate(d.bill_date);
      setTotal(d.total_amount === null || d.total_amount === undefined ? '' : String(d.total_amount));
      setVat(d.vat_amount === null || d.vat_amount === undefined ? '' : String(d.vat_amount));
      setWht(d.wht_amount === null || d.wht_amount === undefined ? '' : String(d.wht_amount));
    } catch (e: any) { setErr(perr(e, t)); } finally { setFilling(false); }
  };
  useEffect(() => { if (upload && !edit) fill(upload.id); }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const nTotal = fin(total), nVat = fin(vat), nWht = fin(wht);
  const numsOk = nTotal !== null && !Number.isNaN(nTotal) && nTotal >= 0
    && (nVat === null || (!Number.isNaN(nVat) && nVat >= 0))
    && (nWht === null || (!Number.isNaN(nWht) && nWht >= 0 && nWht <= nTotal));
  const whtOver = nTotal !== null && nWht !== null && !Number.isNaN(nTotal) && !Number.isNaN(nWht) && nWht > nTotal;
  // The register's own rule (to_pay = total − WHT), shown so the operator sees what the server will store.
  const toPay = numsOk ? r2((nTotal as number) - (nWht || 0)) : null;
  const curBad = !curOk(currency);
  const ready = noteOnly || (!!vendor.trim() && !!billNo.trim() && isoDate(billDate) && (!dueDate || isoDate(dueDate)) && numsOk
    && [vendor, taxId, billNo].every(okStr) && !curBad && !filling);
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    const body: AwPayableBody = {
      vendor_name: vendor.trim(), vendor_tax_id: taxId.trim(), bill_no: billNo.trim(), bill_date: billDate, due_date: dueDate,
      total_amount: r2(nTotal as number), vat_amount: r2(nVat || 0), wht_amount: r2(nWht || 0), note: noteTxt.trim(),
    };
    if (currency.trim()) body.currency = currency.trim().toUpperCase();
    if (!edit && uploadId) body.expense_upload_id = uploadId;
    try {
      if (noteOnly) await api.awPayableUpdate(ctx.clientId, payable!.id, { note: noteTxt.trim() });
      else if (edit) await api.awPayableUpdate(ctx.clientId, payable!.id, body);
      else await api.awPayableCreate(ctx.clientId, body);
      onSaved();
    } catch (e: any) { setErr(perr(e, t)); setBusy(false); }
  };
  return (
    <Dialog title={edit ? t('aw_pay_edit_title') : t('aw_pay_new_title')} onClose={onClose} busy={busy} width={620}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {!edit && (
          <div>
            <label style={lbl}>{t('aw_pay_from_upload')}</label>
            {options === null ? <Loader /> : (
              <select value={uploadId} onChange={e => fill(e.target.value)} style={field} disabled={filling}>
                <option value="">{t('aw_pay_from_upload_none')}</option>
                {options.map(u => <option key={u.id} value={u.id}>{uploadLabel(u)}</option>)}
              </select>
            )}
            <div style={note}>{filling ? <><i className="ti ti-loader" aria-hidden /> {t('aw_pay_filling')}</> : t('aw_pay_from_upload_hint')}</div>
          </div>
        )}
        {edit && payable?.expense_filename && <div style={note}><i className="ti ti-paperclip" aria-hidden /> {t('aw_pay_linked_upload')}: {payable.expense_filename}</div>}
        {noteOnly && <div style={note}><i className="ti ti-lock" aria-hidden /> {t('aw_pay_note_only_hint')}</div>}
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
          <Field label={t('aw_pay_vendor')}><input value={vendor} onChange={e => setVendor(e.target.value)} maxLength={MAX_STR} disabled={noteOnly} style={field} /></Field>
          <Field label={t('aw_pay_vendor_tax_id')}><input value={taxId} onChange={e => setTaxId(e.target.value)} maxLength={MAX_STR} disabled={noteOnly} style={field} /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label={t('aw_pay_bill_no')}><input value={billNo} onChange={e => setBillNo(e.target.value)} maxLength={MAX_STR} disabled={noteOnly} style={field} /></Field>
          <Field label={t('aw_pay_bill_date')}><input type="date" value={billDate} onChange={e => setBillDate(e.target.value)} disabled={noteOnly} style={field} /></Field>
          <Field label={t('aw_pay_due_date')}><input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} disabled={noteOnly} style={field} /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 10 }}>
          <Field label={t('aw_col_currency')}>
            <input value={currency} onChange={e => setCurrency(e.target.value)} maxLength={3} placeholder={t('aw_pay_currency_default')} disabled={noteOnly} style={field} />
            <div style={{ ...note, marginTop: 3, color: curBad ? '#B42318' : undefined }}>{t('aw_pay_currency_hint')}</div>
          </Field>
          <Field label={t('aw_pay_total')}><input type="number" step="0.01" min="0" value={total} onChange={e => setTotal(e.target.value)} disabled={noteOnly} style={field} /></Field>
          <Field label={t('aw_pay_vat')}><input type="number" step="0.01" min="0" value={vat} onChange={e => setVat(e.target.value)} disabled={noteOnly} style={field} /></Field>
          <Field label={t('aw_pay_wht')}><input type="number" step="0.01" min="0" value={wht} onChange={e => setWht(e.target.value)} disabled={noteOnly} style={field} /></Field>
        </div>
        {whtOver && <div style={{ fontSize: 11.5, color: '#B42318' }}>{t('aw_pay_wht_over')}</div>}
        <div style={{ ...card, display: 'flex', alignItems: 'baseline', gap: 10, padding: '9px 13px' }}>
          <span style={{ fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.04em', color: 'var(--text-tertiary)' }}>{t('aw_pay_to_pay')}</span>
          <span style={{ fontSize: 16, fontWeight: 800, fontVariantNumeric: 'tabular-nums' }}>{toPay === null ? '—' : amt(toPay)}</span>
          <span style={note}>{t('aw_pay_to_pay_rule')}</span>
        </div>
        <Field label={t('aw_bank_note_opt')}><textarea value={noteTxt} onChange={e => setNoteTxt(e.target.value)} rows={2} style={{ ...field, resize: 'vertical' }} /></Field>
        <div style={note}>{t('aw_pay_entered_note')}</div>
        {err && <div style={errBox}>{err}</div>}
        <Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon={edit ? 'ti ti-device-floppy' : 'ti ti-plus'} label={edit ? t('aw_bank_save') : t('aw_pay_create_btn')} t={t} />
      </div>
    </Dialog>
  );
}

// ── Record payment dialog ───────────────────────────────────────────────────────────────────────
function PaymentDialog({ ctx, payable, onClose, onDone }: { ctx: AwCtx; payable: AwPayable; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [paidDate, setPaidDate] = useState(todayIso());
  const [amount, setAmount] = useState(String(payable.outstanding));
  const [method, setMethod] = useState<AwPaymentMethod>('transfer');
  const [reference, setReference] = useState('');
  const [noteTxt, setNoteTxt] = useState('');
  const [lineId, setLineId] = useState('');
  const [lines, setLines] = useState<AwBankLine[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  // The unmatched debit lines of the company's statements (every page): paying "from" one records the
  // payment AND marks the line (the B2 rule), the same door as matching the line to this bill in Banking.
  useEffect(() => {
    let alive = true;
    loadUnmatchedDebitLines(ctx.clientId)
      .then(rows => { if (alive) setLines(rows); })
      .catch(() => { if (alive) setLines([]); });
    return () => { alive = false; };
  }, [ctx.clientId]);
  const n = Number(amount);
  // The server's cap is the outstanding itself (amount ≤ outstanding + half a satang, i.e. no 2-dp amount
  // above it); compared here at 2 dp so outstanding + 0.01 is refused before the request, as it is there.
  const max = r2(Number(payable.outstanding));
  const sorted = useMemo(() => {
    const a = Number.isFinite(n) ? r2(n) : r2(Number(payable.outstanding));
    // Lines of the same amount first (what the spec asks for), the rest by date, newest first.
    return [...(lines || [])].sort((x, y) => Number(r2(Number(y.debit)) === a) - Number(r2(Number(x.debit)) === a) || String(y.txn_date).localeCompare(String(x.txn_date)));
  }, [lines, n, payable.outstanding]);
  const pickLine = (id: string) => {
    setLineId(id);
    const l = (lines || []).find(x => x.id === id);
    if (!l) return;
    // The payment takes the line's date and, when the line is not larger than what is owed, its amount;
    // a line that settled several bills keeps the typed part (the field stays editable).
    setPaidDate(l.txn_date);
    if (r2(Number(l.debit)) <= max) setAmount(String(r2(Number(l.debit))));
  };
  const ready = isoDate(paidDate) && Number.isFinite(n) && n > 0 && r2(n) <= max && METHODS.includes(method) && okStr(reference);
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      await api.awPayablePay(ctx.clientId, payable.id, { paid_date: paidDate, amount: r2(n), method, bank_line_id: lineId || undefined, reference: reference.trim() || undefined, note: noteTxt.trim() || undefined });
      onDone();
    } catch (e: any) { setErr(perr(e, t)); setBusy(false); }
  };
  return (
    <Dialog title={t('aw_pay_record_title')} onClose={onClose} busy={busy} width={600}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ ...card, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 12.5 }}>
          <span style={{ fontWeight: 700 }}>{payable.bill_no}</span><span style={{ flex: '1 1 160px' }}>{payable.vendor_name}</span>
          <span style={{ whiteSpace: 'nowrap' }}>{t('aw_col_outstanding')}: <b style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(payable.outstanding)} {payable.currency}</b></span>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label={t('aw_pay_paid_date')}><input type="date" value={paidDate} onChange={e => setPaidDate(e.target.value)} style={field} /></Field>
          <Field label={t('aw_bank_amount')}>
            <input type="number" step="0.01" min="0" max={max} value={amount} onChange={e => setAmount(e.target.value)} style={field} />
            {Number.isFinite(n) && r2(n) > max && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }}>{t('aw_pay_amount_over', { max: amt(payable.outstanding) })}</div>}
          </Field>
          <Field label={t('aw_pay_method')}>
            <select value={method} onChange={e => setMethod(e.target.value as AwPaymentMethod)} style={field}>
              {METHODS.map(m => <option key={m} value={m}>{t(`aw_pay_method_${m}`)}</option>)}
            </select>
          </Field>
        </div>
        <div>
          <label style={lbl}>{t('aw_pay_from_line')}</label>
          {lines === null ? <Loader /> : (
            <select value={lineId} onChange={e => pickLine(e.target.value)} style={field}>
              <option value="">{t('aw_pay_from_line_none')}</option>
              {sorted.map(l => (
                <option key={l.id} value={l.id}>
                  {dmy(l.txn_date)} · {l.account_number} · {l.description} · {amt(l.debit)}{r2(Number(l.debit)) === r2(Number.isFinite(n) ? n : 0) ? ` · ${t('aw_bank_exact')}` : ''}
                </option>
              ))}
            </select>
          )}
          <div style={note}>{t('aw_pay_from_line_hint')}</div>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label={t('aw_pay_reference')}><input value={reference} onChange={e => setReference(e.target.value)} maxLength={MAX_STR} style={field} /></Field>
          <Field label={t('aw_bank_note_opt')}><input value={noteTxt} onChange={e => setNoteTxt(e.target.value)} style={field} /></Field>
        </div>
        {err && <div style={errBox}>{err}</div>}
        <Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-cash" label={t('aw_pay_record_btn')} t={t} />
      </div>
    </Dialog>
  );
}

// ── New debit / credit note dialog ──────────────────────────────────────────────────────────────
function NoteDialog({ ctx, payable, onClose, onDone }: { ctx: AwCtx; payable?: AwPayable; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [options, setOptions] = useState<AwPayable[] | null>(payable ? [payable] : null);
  const [payableId, setPayableId] = useState(payable?.id || '');
  const [kind, setKind] = useState<AwPurchaseNoteKind>('credit');
  const [noteNo, setNoteNo] = useState('');
  const [noteDate, setNoteDate] = useState(todayIso());
  const [amount, setAmount] = useState('');
  const [vat, setVat] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    if (payable) return;
    let alive = true;
    loadAllPayables(ctx.clientId).then(rows => { if (alive) setOptions(rows.filter(isLive)); }).catch((e: any) => { if (alive) { setOptions([]); setErr(perr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId]);   // eslint-disable-line react-hooks/exhaustive-deps
  const p = (options || []).find(x => x.id === payableId) || null;
  const n = Number(amount), nVat = fin(vat);
  // The server's credit cap (credit_total + amount ≤ to_pay, no 2-dp sum above it) checked here too, so the refusal is read before the request.
  const creditOver = !!p && kind === 'credit' && Number.isFinite(n) && r2(Number(p.credit_total) + n) > r2(Number(p.to_pay));
  const ready = !!p && Number.isFinite(n) && n > 0 && !creditOver && (nVat === null || (!Number.isNaN(nVat) && nVat >= 0)) && isoDate(noteDate) && !!reason.trim() && okStr(noteNo) && okStr(reason);
  const submit = async () => {
    if (!ready || !p || busy) return;
    setBusy(true); setErr('');
    try {
      await api.awPayableNoteAdd(ctx.clientId, p.id, { kind, note_no: noteNo.trim() || undefined, note_date: noteDate, amount: r2(n), vat_amount: r2(nVat || 0), reason: reason.trim() });
      onDone();
    } catch (e: any) { setErr(perr(e, t)); setBusy(false); }
  };
  return (
    <Dialog title={t('aw_note_new_title')} onClose={onClose} busy={busy} width={580}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <label style={lbl}>{t('aw_note_payable')}</label>
          {options === null ? <Loader /> : payable ? (
            <div style={{ ...card, padding: '8px 12px', fontSize: 12.5 }}><b>{payable.bill_no}</b> · {payable.vendor_name} · {t('aw_col_outstanding')} {amt(payable.outstanding)} {payable.currency}</div>
          ) : (
            <select value={payableId} onChange={e => setPayableId(e.target.value)} style={field}>
              <option value="">{t('aw_note_pick_payable')}</option>
              {options.map(x => <option key={x.id} value={x.id}>{payableLabel(x)}</option>)}
            </select>
          )}
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label={t('aw_col_type')}>
            <div style={{ display: 'flex', gap: 6 }}>
              <button type="button" onClick={() => setKind('credit')} style={seg(kind === 'credit')}>{t('aw_note_kind_credit')}</button>
              <button type="button" onClick={() => setKind('debit')} style={seg(kind === 'debit')}>{t('aw_note_kind_debit')}</button>
            </div>
          </Field>
          <Field label={t('aw_note_no')}><input value={noteNo} onChange={e => setNoteNo(e.target.value)} maxLength={MAX_STR} style={field} /></Field>
          <Field label={t('aw_col_date')}><input type="date" value={noteDate} onChange={e => setNoteDate(e.target.value)} style={field} /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
          <Field label={t('aw_bank_amount')}>
            <input type="number" step="0.01" min="0" value={amount} onChange={e => setAmount(e.target.value)} style={field} />
            {creditOver && p && <div style={{ fontSize: 11.5, color: '#B42318', marginTop: 3 }}>{t('aw_note_credit_cap', { max: amt(p.to_pay), used: amt(p.credit_total) })}</div>}
          </Field>
          <Field label={t('aw_pay_vat')}><input type="number" step="0.01" min="0" value={vat} onChange={e => setVat(e.target.value)} style={field} /></Field>
        </div>
        <Field label={t('aw_note_reason')}><textarea value={reason} onChange={e => setReason(e.target.value)} rows={2} maxLength={MAX_STR} style={{ ...field, resize: 'vertical' }} /></Field>
        <div style={note}>{t('aw_note_rule')}</div>
        {err && <div style={errBox}>{err}</div>}
        <Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-file-plus" label={t('aw_note_create_btn')} t={t} />
      </div>
    </Dialog>
  );
}

// ── Reason dialog (void a bill · cancel a PO) ───────────────────────────────────────────────────
function ReasonDialog({ title, line, hint, icon, label, onClose, onSubmit }: {
  title: string; line: React.ReactNode; hint: string; icon: string; label: string; onClose: () => void; onSubmit: (reason: string) => Promise<unknown>;
}) {
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const ready = !!reason.trim() && okStr(reason);
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try { await onSubmit(reason.trim()); } catch (e: any) { setErr(perr(e, t)); setBusy(false); }
  };
  return (
    <Dialog title={title} onClose={onClose} busy={busy} width={460}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>{line}</div>
        <div>
          <label style={lbl}>{t('aw_bank_ignore_reason')}</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} maxLength={MAX_STR} style={{ ...field, resize: 'vertical' }} />
          <div style={note}>{hint}</div>
        </div>
        {err && <div style={errBox}>{err}</div>}
        <Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon={icon} label={label} t={t} />
      </div>
    </Dialog>
  );
}

// ── Payable detail drawer ───────────────────────────────────────────────────────────────────────
function PayableDrawer({ ctx, id, onClose, onPay, onNote, onEdit }: { ctx: AwCtx; id: string; onClose: () => void; onPay: (p: AwPayable) => void; onNote: (p: AwPayable) => void; onEdit: (p: AwPayable) => void }) {
  const { t } = useTranslation();
  const [d, setD] = useState<AwPayableDetail | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  useEffect(() => {
    let alive = true;
    api.awPayable(ctx.clientId, id).then((x: AwPayableDetail) => { if (alive) { setD(x); setErr(''); } }).catch((e: any) => { if (alive) setErr(perr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, id, ctx.reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Every write re-reads through ctx.bump — the workspace's one signal — so the list behind the drawer moves too.
  const act = async (key: string, fn: () => Promise<unknown>) => {
    if (busy) return; setBusy(key); setErr('');
    try { await fn(); ctx.bump(); } catch (e: any) { setErr(perr(e, t)); } finally { setBusy(''); }
  };
  const p = d?.payable;
  const Row = ({ k, v }: { k: string; v: React.ReactNode }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12.5, padding: '3px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ color: 'var(--text-secondary)' }}>{k}</span><span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 600, textAlign: 'right' }}>{v}</span>
    </div>
  );
  return (
    <Drawer title={p ? <span>{p.bill_no} <span style={{ fontWeight: 500, color: 'var(--text-secondary)' }}>· {p.vendor_name}</span> <span style={{ ...payPill(p.status), marginLeft: 6 }}>{t(`aw_pay_status_${p.status}`)}</span></span> : t('aw_pay_detail_title')} onClose={onClose}>
      {err && <div style={errBox}>{err}</div>}
      {!d || !p ? (!err && <Loader />) : (
        <>
          <div style={card}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 18px' }}>
              <Row k={t('aw_pay_bill_date')} v={dmy(p.bill_date)} />
              {/* Days overdue only while the bill still owes — a paid or void bill past its due date is not late (the table's rule). */}
              <Row k={t('aw_pay_due_date')} v={p.due_date ? <>{dmy(p.due_date)}{isLive(p) && p.days_overdue > 0 && <span style={{ ...overduePill(p.days_overdue), marginLeft: 6 }}>{t('aw_ar_days', { n: p.days_overdue })}</span>}</> : '—'} />
              <Row k={t('aw_pay_total')} v={`${amt(p.total_amount)} ${p.currency}`} />
              <Row k={t('aw_pay_vat')} v={amt(p.vat_amount)} />
              <Row k={t('aw_pay_wht')} v={amt(p.wht_amount)} />
              <Row k={t('aw_pay_to_pay')} v={amt(p.to_pay)} />
              <Row k={t('aw_col_paid')} v={amt(p.paid_total)} />
              <Row k={t('aw_note_credit_total')} v={amt(p.credit_total)} />
              <Row k={t('aw_note_debit_total')} v={amt(p.debit_total)} />
              <Row k={t('aw_col_outstanding')} v={<b style={{ color: Number(p.outstanding) > 0 ? '#92400E' : 'inherit' }}>{amt(p.outstanding)}</b>} />
            </div>
            <div style={{ ...note, marginTop: 8 }}>
              {p.vendor_tax_id && <span>{t('aw_pay_vendor_tax_id')}: {p.vendor_tax_id} · </span>}
              {p.expense_filename && <span><i className="ti ti-paperclip" aria-hidden /> {t('aw_pay_linked_upload')}: {p.expense_filename} · </span>}
              {p.po_no && <span>{t('aw_po_no')}: {p.po_no} · </span>}
              {p.created_by_name && <span>{p.created_by_name} · {when(p.created_at)}</span>}
            </div>
            {p.note && <div style={{ fontSize: 12.5, marginTop: 6, whiteSpace: 'pre-wrap' }}>{p.note}</div>}
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              {isLive(p) && <button onClick={() => onPay(p)} style={{ ...btnPrimary, padding: '5px 11px', fontSize: 12 }}><i className="ti ti-cash" aria-hidden /> {t('aw_pay_record_btn_short')}</button>}
              {isLive(p) && <button onClick={() => onNote(p)} style={actBtn}><i className="ti ti-file-plus" aria-hidden /> {t('aw_note_add_btn')}</button>}
              {/* Every bill can be edited: a paid or void one opens note-only, as the server allows. */}
              <button onClick={() => onEdit(p)} style={actBtn}><i className="ti ti-pencil" aria-hidden /> {t('aw_profile_edit')}</button>
            </div>
          </div>
          <div>
            <div style={sectionHead}>{t('aw_pay_payments')}</div>
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              {!d.payments.length ? <div style={emptyLine}>{t('aw_pay_no_payments')}</div> : (
                <table style={table}>
                  <thead><tr><th style={th}>{t('aw_col_date')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_amount')}</th><th style={th}>{t('aw_pay_method')}</th><th style={th}>{t('aw_pay_reference')}</th><th style={th}>{t('aw_pay_by')}</th><th style={th}>{t('aw_col_actions')}</th></tr></thead>
                  <tbody>
                    {d.payments.map(x => (
                      <tr key={x.id}>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(x.paid_date)}</td>
                        <td style={num}>{amt(x.amount)}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{t(`aw_pay_method_${x.method}`) || x.method}{x.bank_line_id && <span style={{ ...pillBlue, marginLeft: 6 }} title={t('aw_pay_from_line_mark')}><i className="ti ti-link" aria-hidden /> {t('aw_bank_match')}</span>}</td>
                        <td style={td}>{x.reference}{x.note && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{x.note}</div>}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{x.created_by_name}<div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{when(x.created_at)}</div></td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          <button onClick={() => { if (window.confirm(t('aw_pay_unpay_confirm'))) act(x.id, () => api.awPayableUnpay(ctx.clientId, p.id, x.id)); }} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }} title={x.bank_line_id ? t('aw_pay_unpay_unmarks') : undefined}>
                            <i className={busy === x.id ? 'ti ti-loader' : 'ti ti-trash'} aria-hidden /> {t('aw_bank_delete')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <div>
            <div style={sectionHead}>{t('aw_note_title')}</div>
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              {!d.notes.length ? <div style={emptyLine}>{t('aw_note_none_for_bill')}</div> : (
                <table style={table}>
                  <thead><tr><th style={th}>{t('aw_col_date')}</th><th style={th}>{t('aw_col_type')}</th><th style={th}>{t('aw_note_no')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_amount')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_pay_vat')}</th><th style={th}>{t('aw_note_reason')}</th><th style={th}>{t('aw_col_actions')}</th></tr></thead>
                  <tbody>
                    {d.notes.map(x => (
                      <tr key={x.id}>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(x.note_date)}</td>
                        <td style={td}><span style={x.kind === 'credit' ? pillGreen : pillAmber}>{t(`aw_note_kind_${x.kind}`)}</span></td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{x.note_no}</td>
                        <td style={num}>{amt(x.amount)}</td>
                        <td style={num}>{amt(x.vat_amount)}</td>
                        <td style={td}>{x.reason}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          <button onClick={() => { if (window.confirm(t('aw_note_delete_confirm'))) act(x.id, () => api.awPayableNoteDelete(ctx.clientId, p.id, x.id)); }} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }}>
                            <i className={busy === x.id ? 'ti ti-loader' : 'ti ti-trash'} aria-hidden /> {t('aw_bank_delete')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <div>
            <div style={sectionHead}>{t('aw_pay_bank_lines')}</div>
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              {!d.bank_lines.length ? <div style={emptyLine}>{t('aw_pay_no_bank_lines')}</div> : (
                <table style={table}>
                  <thead><tr><th style={th}>{t('aw_col_date')}</th><th style={th}>{t('aw_bank_account')}</th><th style={th}>{t('aw_bank_description')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_debit')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_match')}</th><th style={th}>{t('aw_col_actions')}</th></tr></thead>
                  <tbody>
                    {d.bank_lines.map(l => (
                      <tr key={l.id}>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(l.txn_date)}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{l.account_number}</td>
                        <td style={td}>{l.description}{l.details && <div style={{ fontSize: 11.5, color: 'var(--text-tertiary)' }}>{l.details}</div>}</td>
                        <td style={num}>{amt(l.debit)}</td>
                        <td style={num}>{amt(l.match_amount)}</td>
                        <td style={{ ...td, whiteSpace: 'nowrap' }}>
                          {/* Unmatching the line reverses what the match recorded here (the payment row), the spec's one door in both directions. */}
                          <button onClick={() => act(l.id, () => api.awBankUnmatch(ctx.clientId, l.id))} disabled={!!busy} style={actBtn} title={t('aw_pay_unmatch_hint')}>
                            <i className={busy === l.id ? 'ti ti-loader' : 'ti ti-link-off'} aria-hidden /> {t('aw_bank_unmatch_btn')}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <div style={note}>{t('aw_pay_register_note')}</div>
        </>
      )}
    </Drawer>
  );
}

// ── Purchases › Payments — the payables register ────────────────────────────────────────────────
export function PaymentsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<AwPayableFilter>('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [data, setData] = useState<AwPayablePage | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [detailId, setDetailId] = useState('');
  const [dlg, setDlg] = useState<{ kind: 'new' } | { kind: 'edit'; p: AwPayable } | { kind: 'pay'; p: AwPayable } | { kind: 'note'; p: AwPayable } | { kind: 'void'; p: AwPayable } | null>(null);
  useEffect(() => { const h = setTimeout(() => { setQd(q); setPage(1); }, 220); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    let alive = true;
    api.awPayables(ctx.clientId, { status, q: qd, from, to, page, limit })
      .then((d: AwPayablePage) => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setData({ rows: [], total: 0, page: 1, limit, summary: { byCurrency: [] } }); setErr(perr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, status, qd, from, to, page, limit]);   // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (id: string, fn: () => Promise<unknown>) => {
    if (busy) return; setBusy(id); setErr('');
    try { await fn(); ctx.bump(); } catch (e: any) { setErr(perr(e, t)); } finally { setBusy(''); }
  };
  const del = (p: AwPayable) => { if (window.confirm(t('aw_pay_delete_confirm', { no: p.bill_no }))) act(p.id, () => api.awPayableDelete(ctx.clientId, p.id)); };
  const chips: OChip[] = PAY_FILTERS.map(s => ({ key: s, label: t(`aw_pay_filter_${s}`), active: s === status, onClick: () => { setStatus(s); setPage(1); } }));
  const rows = data?.rows || [];
  const summary = data?.summary?.byCurrency || [];
  const done = () => { setDlg(null); ctx.bump(); };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <OverflowChips chips={chips} chipStyle={chip} activeStyle={chipOn} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPage(1); }} title={t('aw_bank_from')} style={inp} />
        <span style={{ color: 'var(--text-tertiary)' }}>–</span>
        <input type="date" value={to} onChange={e => { setTo(e.target.value); setPage(1); }} title={t('aw_bank_to')} style={inp} />
        {(from || to) && <button onClick={() => { setFrom(''); setTo(''); setPage(1); }} title={t('aw_bank_clear_dates')} style={{ ...actBtn, padding: '4px 7px' }}><i className="ti ti-x" aria-hidden /></button>}
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('aw_pay_search_ph')} style={{ ...inp, flex: '1 1 200px', maxWidth: 360 }} />
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{data ? data.total : ''}</div>
      </div>
      {err && <div style={errBox}>{err}</div>}
      {/* ⛔ One card per currency, never a total across them (the owner console's rule, kept by /payables.summary). */}
      {summary.length > 0 && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {summary.map(s => (
            <Stat key={s.currency} label={`${t('aw_pay_sum_outstanding')} · ${s.currency}`} value={amt(s.outstanding)}
              sub={`${t('aw_pay_sum_open', { n: s.open_count })}${Number(s.overdue) ? ` · ${t('aw_pay_sum_overdue')}: ${amt(s.overdue)}` : ''}`}
              tone={Number(s.overdue) ? '#92400E' : undefined} />
          ))}
        </div>
      )}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {data === null ? <Loader /> : !rows.length ? <div style={emptyLine}>{t('aw_pay_none')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('aw_pay_bill_no')}</th><th style={th}>{t('aw_pay_vendor')}</th><th style={th}>{t('aw_pay_bill_date')}</th><th style={th}>{t('aw_col_due')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_pay_total')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_pay_to_pay')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_col_paid')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_col_outstanding')}</th>
                <th style={th}>{t('aw_col_status')}</th><th style={th}>{t('aw_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(p => (
                <tr key={p.id}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => setDetailId(p.id)} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--accent)', fontSize: 12.5 }}>{p.bill_no}</button>
                    {(p.expense_filename || p.po_no) && (
                      <div style={{ display: 'flex', gap: 4, marginTop: 3, flexWrap: 'wrap' }}>
                        {p.expense_filename && <span style={pillGrey} title={p.expense_filename}><i className="ti ti-paperclip" aria-hidden /> {t('aw_pay_linked_upload_short')}</span>}
                        {p.po_no && <span style={pillGrey}>{p.po_no}</span>}
                      </div>
                    )}
                  </td>
                  <td style={td}>{p.vendor_name}{p.vendor_tax_id && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{p.vendor_tax_id}</div>}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(p.bill_date)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {p.due_date ? dmy(p.due_date) : '—'}
                    {isLive(p) && p.days_overdue > 0 && <div><span style={overduePill(p.days_overdue)}>{t('aw_ar_days', { n: p.days_overdue })}</span></div>}
                  </td>
                  <td style={num}>{amt(p.total_amount)} <span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>{p.currency}</span></td>
                  <td style={num}>{amt(p.to_pay)}</td>
                  <td style={num}>{amt(p.paid_total)}</td>
                  <td style={{ ...num, fontWeight: 700, color: isLive(p) && Number(p.outstanding) > 0 ? '#92400E' : undefined }}>{amt(p.outstanding)}</td>
                  <td style={td}><span style={payPill(p.status)}>{t(`aw_pay_status_${p.status}`)}</span></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                      {isLive(p) && <button onClick={() => setDlg({ kind: 'pay', p })} disabled={!!busy} style={{ ...btnPrimary, padding: '4px 9px', fontSize: 12 }}><i className="ti ti-cash" aria-hidden /> {t('aw_pay_record_btn_short')}</button>}
                      {isLive(p) && <button onClick={() => setDlg({ kind: 'note', p })} disabled={!!busy} style={actBtn}><i className="ti ti-file-plus" aria-hidden /> {t('aw_note_add_btn')}</button>}
                      {/* Edit is offered for every bill: on a paid or void one the form is note-only (the server accepts the note alone). */}
                      <button onClick={() => setDlg({ kind: 'edit', p })} disabled={!!busy} style={actBtn}><i className="ti ti-pencil" aria-hidden /> {t('aw_profile_edit')}</button>
                      {isLive(p) && <button onClick={() => setDlg({ kind: 'void', p })} disabled={!!busy} style={actBtn}><i className="ti ti-ban" aria-hidden /> {t('aw_pay_void_btn')}</button>}
                      {/* Delete is only offered where the server would accept it: a bill with no payment and no note. */}
                      {!Number(p.paid_total) && !Number(p.credit_total) && !Number(p.debit_total) && (
                        <button onClick={() => del(p)} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }}><i className={busy === p.id ? 'ti ti-loader' : 'ti ti-trash'} aria-hidden /> {t('aw_bank_delete')}</button>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data && <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <div style={note}>{t('aw_pay_register_note')}</div>

      <Fab onClick={() => setDlg({ kind: 'new' })} title={t('aw_pay_new_title')} />
      {detailId && <PayableDrawer ctx={ctx} id={detailId} onClose={() => setDetailId('')} onPay={p => setDlg({ kind: 'pay', p })} onNote={p => setDlg({ kind: 'note', p })} onEdit={p => setDlg({ kind: 'edit', p })} />}
      {dlg?.kind === 'new' && <BillDialog ctx={ctx} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.kind === 'edit' && <BillDialog ctx={ctx} payable={dlg.p} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.kind === 'pay' && <PaymentDialog ctx={ctx} payable={dlg.p} onClose={() => setDlg(null)} onDone={done} />}
      {dlg?.kind === 'note' && <NoteDialog ctx={ctx} payable={dlg.p} onClose={() => setDlg(null)} onDone={done} />}
      {dlg?.kind === 'void' && (
        <ReasonDialog title={t('aw_pay_void_title')} line={<><b style={{ color: 'var(--text-primary)' }}>{dlg.p.bill_no}</b> · {dlg.p.vendor_name} · {amt(dlg.p.to_pay)} {dlg.p.currency}</>}
          hint={t('aw_pay_void_hint')} icon="ti ti-ban" label={t('aw_pay_void_btn')} onClose={() => setDlg(null)}
          onSubmit={async reason => { await api.awPayableVoid(ctx.clientId, dlg.p.id, reason); done(); }} />
      )}
    </div>
  );
}

// ── Purchases › Debit & credit notes — every note across the payables ───────────────────────────
// Read from GET /purchase-notes (the server half's additive list: server paging, kind and search
// filters). The contract itself (SPEC-B3 §2) has no notes list, so when that route answers 404 the
// pane gathers the notes from the bills that carry any — the register's credit_total / debit_total
// caches say which — a few details at a time, and pages them here.
const NOTE_FILTERS = ['all', 'credit', 'debit'] as const;
type NoteFilter = typeof NOTE_FILTERS[number];
const noteMatches = (n: AwPurchaseNoteRow, q: string) => { const k = q.trim().toLowerCase(); return !k || [n.note_no, n.reason, n.bill_no, n.vendor_name].some(v => String(v || '').toLowerCase().includes(k)); };
export function DebitCreditNotesPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [kind, setKind] = useState<NoteFilter>('all');
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [data, setData] = useState<AwPurchaseNotePage | null>(null);
  const [fallback, setFallback] = useState(false);   // true once /purchase-notes answered 404: the fan-out is used from then on
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [dlg, setDlg] = useState(false);
  useEffect(() => { const h = setTimeout(() => { setQd(q); setPage(1); }, 220); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    let alive = true;
    const gather = async (): Promise<AwPurchaseNotePage> => {
      const pays = (await loadAllPayables(ctx.clientId)).filter(p => Number(p.credit_total) > 0 || Number(p.debit_total) > 0);
      const out: AwPurchaseNoteRow[] = [];
      for (let i = 0; i < pays.length; i += 6) {
        const details: AwPayableDetail[] = await Promise.all(pays.slice(i, i + 6).map(p => api.awPayable(ctx.clientId, p.id)));
        for (const d of details) for (const n of d.notes || []) out.push({ ...n, bill_no: d.payable.bill_no, vendor_name: d.payable.vendor_name, currency: d.payable.currency });
      }
      const rows = out.filter(n => (kind === 'all' || n.kind === kind) && noteMatches(n, qd))
        .sort((a, b) => String(b.note_date).localeCompare(String(a.note_date)) || Number(b.created_at) - Number(a.created_at));
      return { rows: rows.slice((page - 1) * limit, page * limit), total: rows.length, page, limit };
    };
    (async () => {
      try {
        let d: AwPurchaseNotePage;
        if (fallback) d = await gather();
        else {
          try { d = await api.awPurchaseNotes(ctx.clientId, { kind: kind === 'all' ? '' : kind, q: qd, page, limit }); }
          catch (e: any) { if (e?.status !== 404) throw e; if (alive) setFallback(true); d = await gather(); }
        }
        if (alive) { setData(d); setErr(''); }
      } catch (e: any) { if (alive) { setData({ rows: [], total: 0, page: 1, limit }); setErr(perr(e, t)); } }
    })();
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, kind, qd, page, limit, fallback]);   // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (n: AwPurchaseNoteRow) => {
    if (busy || !window.confirm(t('aw_note_delete_confirm'))) return;
    setBusy(n.id); setErr('');
    try { await api.awPayableNoteDelete(ctx.clientId, n.payable_id, n.id); ctx.bump(); } catch (e: any) { setErr(perr(e, t)); } finally { setBusy(''); }
  };
  const chips: OChip[] = NOTE_FILTERS.map(k => ({ key: k, label: t(`aw_note_filter_${k}`), active: k === kind, onClick: () => { setKind(k); setPage(1); } }));
  const rows = data?.rows || [];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <OverflowChips chips={chips} chipStyle={chip} activeStyle={chipOn} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('aw_note_search_ph')} style={{ ...inp, flex: '1 1 200px', maxWidth: 360 }} />
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{data ? data.total : ''}</div>
      </div>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {data === null ? <Loader /> : !rows.length ? <div style={emptyLine}>{t('aw_note_none')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('aw_col_date')}</th><th style={th}>{t('aw_col_type')}</th><th style={th}>{t('aw_note_no')}</th><th style={th}>{t('aw_pay_bill_no')}</th><th style={th}>{t('aw_pay_vendor')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_amount')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_pay_vat')}</th><th style={th}>{t('aw_note_reason')}</th><th style={th}>{t('aw_pay_by')}</th><th style={th}>{t('aw_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(n => (
                <tr key={n.id}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(n.note_date)}</td>
                  <td style={td}><span style={n.kind === 'credit' ? pillGreen : pillAmber}>{t(`aw_note_kind_${n.kind}`)}</span></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{n.note_no}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap', fontWeight: 700 }}>{n.bill_no}</td>
                  <td style={td}>{n.vendor_name}</td>
                  <td style={num}>{amt(n.amount)} <span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>{n.currency}</span></td>
                  <td style={num}>{amt(n.vat_amount)}</td>
                  <td style={td}>{n.reason}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{n.created_by_name}<div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{when(n.created_at)}</div></td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => del(n)} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }}><i className={busy === n.id ? 'ti ti-loader' : 'ti ti-trash'} aria-hidden /> {t('aw_bank_delete')}</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data && <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <div style={note}>{t('aw_note_rule')}</div>
      <Fab onClick={() => setDlg(true)} title={t('aw_note_new_title')} />
      {dlg && <NoteDialog ctx={ctx} onClose={() => setDlg(false)} onDone={() => { setDlg(false); ctx.bump(); }} />}
    </div>
  );
}

// ── Purchase order dialog (new · edit while draft) with the lines editor ────────────────────────
type LineDraft = { description: string; qty: string; unit_price: string };
const blankLine = (): LineDraft => ({ description: '', qty: '1', unit_price: '' });
const lineAmount = (l: LineDraft): number | null => {
  const q = fin(l.qty), u = fin(l.unit_price);
  if (q === null || u === null || Number.isNaN(q) || Number.isNaN(u)) return null;
  // The PO's own rule: line = qty × unit price AS TYPED, rounded to 2 dp — exactly what the server stores
  // (poLinesOf keeps the unit price as typed and rounds only the amount), so the total the operator
  // approves is the total the register holds. The unit price is therefore SENT unrounded below.
  return r2(q * u);
};
function PoDialog({ ctx, detail, onClose, onSaved }: { ctx: AwCtx; detail?: AwPurchaseOrderDetail; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const po = detail?.po;
  const [vendor, setVendor] = useState(po?.vendor_name || '');
  const [taxId, setTaxId] = useState(po?.vendor_tax_id || '');
  const [poDate, setPoDate] = useState(po?.po_date || todayIso());
  const [expected, setExpected] = useState(po?.expected_date || '');
  const [currency, setCurrency] = useState(po?.currency || '');   // blank = the company's default, applied by the server
  const [noteTxt, setNoteTxt] = useState(po?.note || '');
  const [lines, setLines] = useState<LineDraft[]>(detail?.lines?.length ? detail.lines.map(l => ({ description: l.description, qty: String(l.qty), unit_price: String(l.unit_price) })) : [blankLine()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const amounts = lines.map(lineAmount);
  const total = amounts.every(a => a !== null) ? r2(amounts.reduce((s, a) => s + (a as number), 0)) : null;
  const lineOk = (l: LineDraft) => { const q = fin(l.qty), u = fin(l.unit_price); return !!l.description.trim() && l.description.length <= 500 && q !== null && !Number.isNaN(q) && q > 0 && u !== null && !Number.isNaN(u) && u >= 0; };
  const curBad = !curOk(currency);
  const ready = !!vendor.trim() && [vendor, taxId].every(okStr) && !curBad && isoDate(poDate) && (!expected || isoDate(expected)) && lines.length >= 1 && lines.every(lineOk);
  const setLine = (i: number, patch: Partial<LineDraft>) => setLines(ls => ls.map((l, j) => j === i ? { ...l, ...patch } : l));
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    const body: AwPurchaseOrderBody = {
      vendor_name: vendor.trim(), vendor_tax_id: taxId.trim(), po_date: poDate, expected_date: expected, note: noteTxt.trim(),
      lines: lines.map((l): AwPurchaseOrderLineBody => ({ description: l.description.trim(), qty: Number(l.qty), unit_price: Number(l.unit_price) })),
    };
    if (currency.trim()) body.currency = currency.trim().toUpperCase();
    try {
      if (po) await api.awPurchaseOrderUpdate(ctx.clientId, po.id, body); else await api.awPurchaseOrderCreate(ctx.clientId, body);
      onSaved();
    } catch (e: any) { setErr(perr(e, t)); setBusy(false); }
  };
  return (
    <Dialog title={po ? `${t('aw_po_edit_title')} · ${po.po_no}` : t('aw_po_new_title')} onClose={onClose} busy={busy} width={760}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: 10 }}>
          <Field label={t('aw_pay_vendor')}><input value={vendor} onChange={e => setVendor(e.target.value)} maxLength={MAX_STR} style={field} /></Field>
          <Field label={t('aw_pay_vendor_tax_id')}><input value={taxId} onChange={e => setTaxId(e.target.value)} maxLength={MAX_STR} style={field} /></Field>
          <Field label={t('aw_col_currency')}>
            <input value={currency} onChange={e => setCurrency(e.target.value)} maxLength={3} placeholder={t('aw_pay_currency_default')} style={field} />
            <div style={{ ...note, marginTop: 3, color: curBad ? '#B42318' : undefined }}>{t('aw_pay_currency_hint')}</div>
          </Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 2fr', gap: 10 }}>
          <Field label={t('aw_po_date')}><input type="date" value={poDate} onChange={e => setPoDate(e.target.value)} style={field} /></Field>
          <Field label={t('aw_po_expected')}><input type="date" value={expected} onChange={e => setExpected(e.target.value)} style={field} /></Field>
          <Field label={t('aw_bank_note_opt')}><input value={noteTxt} onChange={e => setNoteTxt(e.target.value)} style={field} /></Field>
        </div>
        <div>
          <label style={lbl}>{t('aw_po_lines')}</label>
          <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
            <table style={table}>
              <thead>
                <tr>
                  <th style={{ ...th, width: 28 }}>#</th><th style={th}>{t('aw_po_line_description')}</th>
                  <th style={{ ...th, textAlign: 'right', width: 110 }}>{t('aw_po_qty')}</th><th style={{ ...th, textAlign: 'right', width: 140 }}>{t('aw_po_unit_price')}</th>
                  <th style={{ ...th, textAlign: 'right', width: 130 }}>{t('aw_bank_amount')}</th><th style={{ ...th, width: 40 }} />
                </tr>
              </thead>
              <tbody>
                {lines.map((l, i) => (
                  <tr key={i}>
                    <td style={{ ...td, color: 'var(--text-tertiary)' }}>{i + 1}</td>
                    <td style={td}><input value={l.description} onChange={e => setLine(i, { description: e.target.value })} maxLength={500} style={field} /></td>
                    <td style={td}><input type="number" step="any" min="0" value={l.qty} onChange={e => setLine(i, { qty: e.target.value })} style={{ ...field, textAlign: 'right' }} /></td>
                    <td style={td}><input type="number" step="any" min="0" value={l.unit_price} onChange={e => setLine(i, { unit_price: e.target.value })} style={{ ...field, textAlign: 'right' }} /></td>
                    <td style={{ ...num, fontWeight: 600 }}>{amounts[i] === null ? '—' : amt(amounts[i])}</td>
                    <td style={td}>
                      <button onClick={() => setLines(ls => ls.length > 1 ? ls.filter((_, j) => j !== i) : [blankLine()])} title={t('aw_po_remove_line')} style={{ ...actBtn, padding: '4px 7px', color: '#B42318' }}><i className="ti ti-x" aria-hidden /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={4} style={{ ...td, borderBottom: 'none' }}>
                    <button onClick={() => setLines(ls => [...ls, blankLine()])} style={actBtn}><i className="ti ti-plus" aria-hidden /> {t('aw_po_add_line')}</button>
                  </td>
                  <td style={{ ...num, borderBottom: 'none', fontWeight: 800, fontSize: 13.5 }}>{total === null ? '—' : amt(total)}</td>
                  <td style={{ ...td, borderBottom: 'none' }} />
                </tr>
              </tfoot>
            </table>
          </div>
          <div style={note}>{t('aw_po_lines_rule')}</div>
        </div>
        {err && <div style={errBox}>{err}</div>}
        <Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon={po ? 'ti ti-device-floppy' : 'ti ti-plus'} label={po ? t('aw_bank_save') : t('aw_po_create_btn')} t={t} />
      </div>
    </Dialog>
  );
}

// ── PO lines (read-only) ────────────────────────────────────────────────────────────────────────
function PoViewDialog({ ctx, id, onClose, onEdit }: { ctx: AwCtx; id: string; onClose: () => void; onEdit: (d: AwPurchaseOrderDetail) => void }) {
  const { t } = useTranslation();
  const [d, setD] = useState<AwPurchaseOrderDetail | null>(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    let alive = true;
    api.awPurchaseOrder(ctx.clientId, id).then((x: AwPurchaseOrderDetail) => { if (alive) setD(x); }).catch((e: any) => { if (alive) setErr(perr(e, t)); });
    return () => { alive = false; };
  }, [ctx.clientId, id, ctx.reload]);   // eslint-disable-line react-hooks/exhaustive-deps
  const po = d?.po;
  return (
    <Dialog title={po ? `${po.po_no} · ${po.vendor_name}` : t('aw_sub_purchase_orders')} onClose={onClose} width={720}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {err && <div style={errBox}>{err}</div>}
        {!d || !po ? (!err && <Loader />) : (
          <>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 12.5, alignItems: 'baseline' }}>
              <span style={poPill(po.status)}>{t(`aw_po_status_${po.status}`)}</span>
              <span>{t('aw_po_date')}: <b>{dmy(po.po_date)}</b></span>
              {po.expected_date && <span>{t('aw_po_expected')}: <b>{dmy(po.expected_date)}</b></span>}
              {po.vendor_tax_id && <span>{t('aw_pay_vendor_tax_id')}: {po.vendor_tax_id}</span>}
              {po.payable_id && <span style={pillGreen}><i className="ti ti-file-invoice" aria-hidden /> {t('aw_po_status_billed')}</span>}
            </div>
            <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
              <table style={table}>
                <thead><tr><th style={{ ...th, width: 28 }}>#</th><th style={th}>{t('aw_po_line_description')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_po_qty')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_po_unit_price')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_bank_amount')}</th></tr></thead>
                <tbody>
                  {d.lines.map(l => (
                    <tr key={l.id}><td style={{ ...td, color: 'var(--text-tertiary)' }}>{l.line_no}</td><td style={td}>{l.description}</td><td style={num}>{l.qty}</td><td style={num}>{unitPrice(l.unit_price)}</td><td style={num}>{amt(l.amount)}</td></tr>
                  ))}
                </tbody>
                <tfoot><tr><td colSpan={4} style={{ ...num, borderBottom: 'none', fontWeight: 700 }}>{t('aw_col_total')}</td><td style={{ ...num, borderBottom: 'none', fontWeight: 800, fontSize: 13.5 }}>{amt(po.total_amount)} <span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>{po.currency}</span></td></tr></tfoot>
              </table>
            </div>
            {po.note && <div style={{ fontSize: 12.5, whiteSpace: 'pre-wrap' }}>{po.note}</div>}
            <div style={note}>{po.created_by_name} · {when(po.created_at)}</div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={onClose} style={cancelBtn}>{t('aw_bank_close')}</button>
              {po.status === 'draft' && <button onClick={() => onEdit(d)} style={btnPrimary}><i className="ti ti-pencil" aria-hidden /> {t('aw_profile_edit')}</button>}
            </div>
          </>
        )}
      </div>
    </Dialog>
  );
}

// ── Convert a PO to a bill ──────────────────────────────────────────────────────────────────────
function ConvertDialog({ ctx, po, onClose, onDone }: { ctx: AwCtx; po: AwPurchaseOrder; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const [billNo, setBillNo] = useState('');
  const [billDate, setBillDate] = useState(todayIso());
  const [dueDate, setDueDate] = useState('');
  const [vat, setVat] = useState('');
  const [wht, setWht] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const nVat = fin(vat), nWht = fin(wht);
  const total = Number(po.total_amount) || 0;
  const whtOver = nWht !== null && !Number.isNaN(nWht) && nWht > total;
  const ready = !!billNo.trim() && okStr(billNo) && isoDate(billDate) && (!dueDate || isoDate(dueDate))
    && (nVat === null || (!Number.isNaN(nVat) && nVat >= 0)) && (nWht === null || (!Number.isNaN(nWht) && nWht >= 0 && !whtOver));
  const toPay = ready ? r2(total - (nWht || 0)) : null;   // the register's rule, shown before the server stores it
  const submit = async () => {
    if (!ready || busy) return;
    setBusy(true); setErr('');
    try {
      await api.awPurchaseOrderConvert(ctx.clientId, po.id, { bill_no: billNo.trim(), bill_date: billDate, due_date: dueDate || undefined, vat_amount: r2(nVat || 0), wht_amount: r2(nWht || 0) });
      onDone();
    } catch (e: any) { setErr(perr(e, t)); setBusy(false); }
  };
  return (
    <Dialog title={t('aw_po_convert_title')} onClose={onClose} busy={busy} width={560}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ ...card, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'baseline', fontSize: 12.5 }}>
          <span style={{ fontWeight: 700 }}>{po.po_no}</span><span style={{ flex: '1 1 160px' }}>{po.vendor_name}</span>
          <span style={{ whiteSpace: 'nowrap' }}>{t('aw_col_total')}: <b style={{ fontVariantNumeric: 'tabular-nums' }}>{amt(po.total_amount)} {po.currency}</b></span>
        </div>
        <div style={note}>{t('aw_po_convert_hint')}</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label={t('aw_pay_bill_no')}><input value={billNo} onChange={e => setBillNo(e.target.value)} maxLength={MAX_STR} style={field} /></Field>
          <Field label={t('aw_pay_bill_date')}><input type="date" value={billDate} onChange={e => setBillDate(e.target.value)} style={field} /></Field>
          <Field label={t('aw_pay_due_date')}><input type="date" value={dueDate} onChange={e => setDueDate(e.target.value)} style={field} /></Field>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10 }}>
          <Field label={t('aw_pay_vat')}><input type="number" step="0.01" min="0" value={vat} onChange={e => setVat(e.target.value)} style={field} /></Field>
          <Field label={t('aw_pay_wht')}><input type="number" step="0.01" min="0" value={wht} onChange={e => setWht(e.target.value)} style={field} /></Field>
          <Field label={t('aw_pay_to_pay')}><div style={{ ...field, fontWeight: 800, fontVariantNumeric: 'tabular-nums', background: 'var(--bg-app)' }}>{toPay === null ? '—' : amt(toPay)}</div></Field>
        </div>
        {whtOver && <div style={{ fontSize: 11.5, color: '#B42318' }}>{t('aw_pay_wht_over')}</div>}
        {err && <div style={errBox}>{err}</div>}
        <Foot onClose={onClose} onSubmit={submit} busy={busy} ready={ready} icon="ti ti-file-invoice" label={t('aw_po_convert_btn')} t={t} />
      </div>
    </Dialog>
  );
}

// ── Purchases › Purchase orders ─────────────────────────────────────────────────────────────────
export function PurchaseOrdersPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<AwPurchaseOrderFilter>('all');
  const [q, setQ] = useState('');
  const [qd, setQd] = useState('');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [data, setData] = useState<AwPurchaseOrderPage | null>(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [dlg, setDlg] = useState<{ kind: 'new' } | { kind: 'edit'; detail: AwPurchaseOrderDetail } | { kind: 'view'; id: string } | { kind: 'convert'; po: AwPurchaseOrder } | { kind: 'cancel'; po: AwPurchaseOrder } | null>(null);
  useEffect(() => { const h = setTimeout(() => { setQd(q); setPage(1); }, 220); return () => clearTimeout(h); }, [q]);
  useEffect(() => {
    let alive = true;
    api.awPurchaseOrders(ctx.clientId, { status: status === 'all' ? '' : status, q: qd, page, limit })
      .then((d: AwPurchaseOrderPage) => { if (alive) { setData(d); setErr(''); } })
      .catch((e: any) => { if (alive) { setData({ rows: [], total: 0, page: 1, limit }); setErr(perr(e, t)); } });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload, status, qd, page, limit]);   // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (id: string, fn: () => Promise<unknown>) => {
    if (busy) return; setBusy(id); setErr('');
    try { await fn(); ctx.bump(); } catch (e: any) { setErr(perr(e, t)); } finally { setBusy(''); }
  };
  // Edit needs the lines, which the list does not carry: fetch the detail, then open the editor (a read — no re-read signal).
  const edit = async (po: AwPurchaseOrder) => {
    if (busy) return; setBusy(po.id); setErr('');
    try { const d: AwPurchaseOrderDetail = await api.awPurchaseOrder(ctx.clientId, po.id); setDlg({ kind: 'edit', detail: d }); }
    catch (e: any) { setErr(perr(e, t)); } finally { setBusy(''); }
  };
  const chips: OChip[] = PO_FILTERS.map(s => ({ key: s, label: t(`aw_po_filter_${s}`), active: s === status, onClick: () => { setStatus(s); setPage(1); } }));
  const rows = data?.rows || [];
  const done = () => { setDlg(null); ctx.bump(); };
  const canConvert = (po: AwPurchaseOrder) => po.status !== 'billed' && po.status !== 'cancelled';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <OverflowChips chips={chips} chipStyle={chip} activeStyle={chipOn} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <input value={q} onChange={e => setQ(e.target.value)} placeholder={t('aw_po_search_ph')} style={{ ...inp, flex: '1 1 200px', maxWidth: 360 }} />
        <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{data ? data.total : ''}</div>
      </div>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {data === null ? <Loader /> : !rows.length ? <div style={emptyLine}>{t('aw_po_none')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('aw_po_no')}</th><th style={th}>{t('aw_pay_vendor')}</th><th style={th}>{t('aw_po_date')}</th><th style={th}>{t('aw_po_expected')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_col_total')}</th><th style={th}>{t('aw_col_status')}</th><th style={{ ...th, textAlign: 'right' }}>{t('aw_po_lines')}</th><th style={th}>{t('aw_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(po => (
                <tr key={po.id}>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <button onClick={() => setDlg({ kind: 'view', id: po.id })} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 700, color: 'var(--accent)', fontSize: 12.5 }}>{po.po_no}</button>
                  </td>
                  <td style={td}>{po.vendor_name}{po.vendor_tax_id && <div style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{po.vendor_tax_id}</div>}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{dmy(po.po_date)}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{po.expected_date ? dmy(po.expected_date) : '—'}</td>
                  <td style={{ ...num, fontWeight: 700 }}>{amt(po.total_amount)} <span style={{ color: 'var(--text-tertiary)', fontWeight: 500 }}>{po.currency}</span></td>
                  <td style={td}><span style={poPill(po.status)}>{t(`aw_po_status_${po.status}`)}</span></td>
                  <td style={num}>{po.line_count}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <span style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}>
                      {po.status === 'draft' && <button onClick={() => edit(po)} disabled={!!busy} style={actBtn}><i className={busy === po.id ? 'ti ti-loader' : 'ti ti-pencil'} aria-hidden /> {t('aw_profile_edit')}</button>}
                      {po.status === 'draft' && <button onClick={() => act(po.id, () => api.awPurchaseOrderApprove(ctx.clientId, po.id))} disabled={!!busy} style={{ ...btnPrimary, padding: '4px 9px', fontSize: 12 }}><i className={busy === po.id ? 'ti ti-loader' : 'ti ti-check'} aria-hidden /> {t('aw_po_approve')}</button>}
                      {po.status === 'approved' && <button onClick={() => act(po.id, () => api.awPurchaseOrderReceive(ctx.clientId, po.id))} disabled={!!busy} style={{ ...btnPrimary, padding: '4px 9px', fontSize: 12 }}><i className={busy === po.id ? 'ti ti-loader' : 'ti ti-package'} aria-hidden /> {t('aw_po_receive')}</button>}
                      {canConvert(po) && <button onClick={() => setDlg({ kind: 'convert', po })} disabled={!!busy} style={actBtn}><i className="ti ti-file-invoice" aria-hidden /> {t('aw_po_convert_btn')}</button>}
                      {canConvert(po) && <button onClick={() => setDlg({ kind: 'cancel', po })} disabled={!!busy} style={{ ...actBtn, color: '#B42318' }}><i className="ti ti-ban" aria-hidden /> {t('aw_po_cancel_btn')}</button>}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data && <Pagination page={page} total={data.total} limit={data.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <div style={note}>{t('aw_po_note')}</div>
      <Fab onClick={() => setDlg({ kind: 'new' })} title={t('aw_po_new_title')} />
      {dlg?.kind === 'new' && <PoDialog ctx={ctx} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.kind === 'edit' && <PoDialog ctx={ctx} detail={dlg.detail} onClose={() => setDlg(null)} onSaved={done} />}
      {dlg?.kind === 'view' && <PoViewDialog ctx={ctx} id={dlg.id} onClose={() => setDlg(null)} onEdit={d => setDlg({ kind: 'edit', detail: d })} />}
      {dlg?.kind === 'convert' && <ConvertDialog ctx={ctx} po={dlg.po} onClose={() => setDlg(null)} onDone={done} />}
      {dlg?.kind === 'cancel' && (
        <ReasonDialog title={t('aw_po_cancel_title')} line={<><b style={{ color: 'var(--text-primary)' }}>{dlg.po.po_no}</b> · {dlg.po.vendor_name} · {amt(dlg.po.total_amount)} {dlg.po.currency}</>}
          hint={t('aw_po_cancel_hint')} icon="ti ti-ban" label={t('aw_po_cancel_btn')} onClose={() => setDlg(null)}
          onSubmit={async reason => { await api.awPurchaseOrderCancel(ctx.clientId, dlg.po.id, reason); done(); }} />
      )}
    </div>
  );
}

// ── Purchases › Supplier bills (the portal uploads) ─────────────────────────────────────────────
// The bills the company uploaded through its own portal (merchant_expense_uploads), as stored:
// pending / approved / rejected with the reject reason, and the OCR vendor / amount / date where the
// portal recorded them. Approval stays the portal's flow. "Create bill" opens the payable form
// pre-filled from the upload's own fields; a "Billed" pill shows the bill once one links to it.
const STATUSES = ['', 'pending', 'approved', 'rejected'] as const;
const sPill = (s: string): React.CSSProperties => s === 'approved' ? pillGreen : s === 'rejected' ? pillRed : pillAmber;
const whenAny = (v: string | number) => typeof v === 'number' ? fmtDateTime(v) : (/^\d+$/.test(String(v || '')) ? fmtDateTime(Number(v)) : String(v || ''));

export function SupplierBillsPane({ ctx }: { ctx: AwCtx }) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<string>('');
  const [rows, setRows] = useState<AwExpense[] | null>(null);
  // Paged like the Sales tables: the server answers total/limit, the pager is driven by its limit.
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(getPageSize());
  const [paging, setPaging] = useState<{ total: number; limit: number }>({ total: 0, limit });
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState('');
  const [billed, setBilled] = useState<Map<string, AwPayable> | null>(null);
  const [creating, setCreating] = useState<AwExpense | null>(null);
  useEffect(() => {
    let alive = true; setRows(null);
    api.awExpenses(ctx.clientId, status, { page, limit }).then((d: any) => { if (alive) { setRows(d.rows || []); setPaging({ total: Number(d.total) || 0, limit: Number(d.limit) || limit }); setErr(''); } }).catch((e: any) => { if (alive) { setRows([]); setErr(e.message); } });
    return () => { alive = false; };
  }, [ctx.clientId, status, page, limit, ctx.reload]);
  // Which uploads already have a bill: read off the payables register (no lookup route in the contract).
  useEffect(() => {
    let alive = true;
    loadAllPayables(ctx.clientId).then(ps => { if (alive) setBilled(byUpload(ps)); }).catch(() => { if (alive) setBilled(new Map()); });
    return () => { alive = false; };
  }, [ctx.clientId, ctx.reload]);
  const openFile = async (r: AwExpense) => {
    if (busy) return; setBusy(r.id); setErr('');
    try { openBlob(await api.awExpenseFileBlobUrl(ctx.clientId, r.id)); } catch (e: any) { setErr(e.message); } finally { setBusy(''); }
  };
  const label = (s: string) => s === '' ? t('aw_exp_all') : t(`aw_exp_${s}`);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {STATUSES.map(s => (
          <button key={s} onClick={() => { setStatus(s); setPage(1); }} style={{ ...btn, padding: '5px 11px', fontSize: 12, ...(status === s ? { background: 'var(--accent)', color: '#fff', border: '1px solid var(--accent)' } : {}) }}>{label(s)}</button>
        ))}
      </div>
      {err && <div style={errBox}>{err}</div>}
      <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
        {rows === null ? <Loader />
          : !rows.length ? <div style={emptyLine}>{t('aw_no_rows')}</div> : (
          <table style={table}>
            <thead>
              <tr>
                <th style={th}>{t('aw_exp_file')}</th><th style={th}>{t('aw_exp_uploaded')}</th><th style={th}>{t('aw_exp_vendor')}</th>
                <th style={{ ...th, textAlign: 'right' }}>{t('aw_exp_amount')}</th><th style={th}>{t('aw_exp_doc_date')}</th>
                <th style={th}>{t('aw_col_status')}</th><th style={th}>{t('aw_exp_reason')}</th><th style={th}>{t('aw_pay_bill')}</th><th style={th}>{t('aw_col_actions')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(r => {
                const link = billed?.get(r.id) || null;
                return (
                  <tr key={r.id}>
                    <td style={{ ...td, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={r.filename}>{r.filename}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{whenAny(r.uploaded_at)}</td>
                    <td style={td}>{r.vendor || ''}</td>
                    <td style={num}>{r.amount === null || r.amount === undefined ? '' : amt(r.amount)}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>{r.doc_date ? dmy(r.doc_date) : ''}</td>
                    <td style={td}><span style={sPill(r.status)}>{t(`aw_exp_${r.status}`) || r.status}</span></td>
                    <td style={{ ...td, color: '#B42318' }}>{r.reject_reason || ''}</td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      {billed === null ? <i className="ti ti-loader" style={{ color: 'var(--text-tertiary)' }} aria-hidden />
                        : link ? <span style={link.status === 'void' ? pillRed : pillGreen} title={`${link.bill_no} · ${amt(link.outstanding)} ${link.currency}`}><i className="ti ti-file-invoice" aria-hidden /> {t('aw_pay_billed')} · {link.bill_no}</span>
                        : (r.status === 'pending' || r.status === 'approved')
                          ? <button onClick={() => setCreating(r)} disabled={!!busy} style={actBtn}><i className="ti ti-file-plus" aria-hidden /> {t('aw_pay_create_from_upload')}</button>
                          : <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                    </td>
                    <td style={{ ...td, whiteSpace: 'nowrap' }}>
                      <button onClick={() => openFile(r)} disabled={!!busy} style={{ ...btn, padding: '4px 9px', fontSize: 12, opacity: busy === r.id ? 0.6 : 1 }}>
                        <i className={busy === r.id ? 'ti ti-loader' : 'ti ti-file-search'} aria-hidden /> {t('aw_exp_open_file')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {rows !== null && <Pagination page={page} total={paging.total} limit={paging.limit || limit} onPage={setPage} onLimit={n => { setLimit(n); setPage(1); }} />}
      <div style={note}>{t('aw_exp_note')}</div>
      {creating && <BillDialog ctx={ctx} upload={creating} onClose={() => setCreating(null)} onSaved={() => { setCreating(null); ctx.bump(); }} />}
    </div>
  );
}
